import { Check } from 'lucide-react'
import type { ReactNode } from 'react'
import { Logo } from '@/components/Logo'
import { cn } from '@/lib/utils'

export interface AuthStep {
  label: string
  state: 'done' | 'current' | 'upcoming'
}

export function AuthLayout({
  steps,
  children,
  wide,
}: {
  steps?: AuthStep[]
  children: ReactNode
  wide?: boolean
}) {
  return (
    <div className="auth-backdrop flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div
        className={cn(
          'w-full rounded-[var(--radius-card)] border border-line/70 bg-paper px-10 py-9 shadow-[var(--shadow-float)]',
          wide ? 'max-w-[420px]' : 'max-w-[380px]',
        )}
      >
        <div className="flex flex-col items-center text-center">
          <Logo />
          <p className="mt-4 text-[13px] font-medium text-ink-soft">
            Historical Dye Experiment Database
          </p>
          <p className="mt-0.5 text-[11px] text-ink-faint">
            An Institutional Tool for Conservation and Archeometry
          </p>
        </div>

        <div className="my-6 h-px bg-line" />

        {steps && <Stepper steps={steps} />}

        {children}
      </div>
    </div>
  )
}

function Stepper({ steps }: { steps: AuthStep[] }) {
  return (
    <ol className="mb-7 flex items-center">
      {steps.map((step, index) => (
        <li key={step.label} className={cn('flex items-center', index > 0 && 'flex-1')}>
          {index > 0 && (
            <span
              className={cn(
                'mx-2 h-px flex-1',
                step.state === 'upcoming' ? 'bg-line-strong' : 'bg-brand',
              )}
            />
          )}
          <span className="flex items-center gap-1.5">
            <span
              className={cn(
                'flex size-[18px] items-center justify-center rounded-full text-[10px] font-semibold',
                step.state === 'upcoming'
                  ? 'bg-line-strong/60 text-ink-muted'
                  : 'bg-brand text-white',
              )}
            >
              {step.state === 'done' ? <Check className="size-3" /> : index + 1}
            </span>
            <span
              className={cn(
                'text-[11px]',
                step.state === 'current'
                  ? 'font-semibold text-ink'
                  : step.state === 'done'
                    ? 'text-ink-soft'
                    : 'text-ink-faint',
              )}
            >
              {step.label}
            </span>
          </span>
        </li>
      ))}
    </ol>
  )
}

export function signUpSteps(current: 1 | 2 | 3): AuthStep[] {
  const labels = ['Account', 'Profile', 'Verify']
  return labels.map((label, index) => ({
    label,
    state: index + 1 < current ? 'done' : index + 1 === current ? 'current' : 'upcoming',
  }))
}
